import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Typography,
  Button,
  LinearProgress,
  linearProgressClasses,
  CircularProgress,
  CircularProgressProps,
  circularProgressClasses,
  Card,
  Container,
  TooltipProps,
  tooltipClasses,
  Tooltip,
  FormGroup,
  Stack,
  Switch,
  Dialog,
  DialogContent,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import styled from "@emotion/styled";
import {
  communication,
  file,
  hubspot,
  humanResource,
  personAdd,
  salesForceImg,
  girlWithLaptop,
  mostPopular,
  illustration,
} from "../../assets/images";
import {
  GroupIcon,
  LaunchIcon,
  PauseIcon,
  PlayIcon,
  CommunicationIcon,
  DatabaseIcon,
  InformationIcon,
  TickSymbolThin,
  CalenderIcon,
  TimerIconMd,
  TickWithCircle,
  AlertIcon,
  InfoMiniIcon,
  InfoBlueIcon,
} from "../../assets/icons";
import CancelAccountModal from "../../modals/CancelAccount";
import CancelSubscription from "../../modals/CancelSubscription";
import { SubscriptionData } from "../../types/chargebee";
import { formatDate, formatPlanName } from "../../utils/common.util";
import ChargebeeService from "../../services/chargebee.service";
import UsageService from "../../services/usage.service";
import StorageIcon from "@mui/icons-material/Storage";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import PricingSection from "./pricing/PricingSection";
import theme from "../../theme";
import { inherits } from "util";
import BillingCycleModal from "../../modals/BillingCycleModal";
import { toast } from "react-toastify";
import { automatedPriceMonthly, automatedPriceYearly, allInOnePriceMonthly, allInOnePriceYearly } from './pricing/pricingData';

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 18,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: "#DDE0E5",
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 16,
    backgroundColor: "#614EFA",
  },
}));

interface CustomDropdownProps {
  title: string;
  options: string[];
  defaultValue: string;
  onSelect: (selected: string) => void;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  title,
  options,
  defaultValue,
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleOptionClick = (value: string) => {
    setSelectedValue(value);
    onSelect(value); // Notify parent of the selection
    setIsOpen(false); // Close the dropdown after selection
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Handle toggling dropdowns and close all other dropdowns
  useEffect(() => {
    if (isOpen) {
      const closeOtherDropdowns = () => {
        document
          .querySelectorAll(".custom-dropdown.open")
          .forEach((dropdown) => {
            if (dropdown !== dropdownRef.current) {
              dropdown.classList.remove("open");
            }
          });
      };
      closeOtherDropdowns();
    }
  }, [isOpen]);

  return (
    <div
      className={`custom-dropdown dropdown ${isOpen ? "open" : ""}`}
      ref={dropdownRef}
    >
      <div className="dropdown-header" onClick={toggleDropdown}>
        <div>
          <span className="title">{title}</span>
          <span className="value">{selectedValue}</span>
        </div>
        <span className={`chevron ${isOpen ? "rotate" : ""}`}></span>
      </div>
      {isOpen && (
        <ul className="dropdown-options">
          {options.map((option, index) => (
            <li
              key={index}
              className={option === selectedValue ? "selected" : ""}
              onClick={() => handleOptionClick(option)}
            >
              {option}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const BorderLinearProgressGreen = styled(LinearProgress)<{ isHighUsage?: boolean }>(({ theme, isHighUsage }) => ({
  height: 8,
  borderRadius: 18,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: "#DDE0E5", // Grey background
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 16,
    backgroundColor: isHighUsage ? "#E44258" : "#22B060", // Red progress if high usage, otherwise green
  },
}));

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: "#2B2B30",
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#2B2B30",
  },
}));

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 15,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(18px)",
      top: "2px",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    top: "2px",
    left: "2px",
    "&.Mui-checked": {
      transform: "translateX(17px)",
      color: "#fff",
      top: "2px",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#22B060",
        // ...theme.applyStyles('dark', {
        //     backgroundColor: '#177ddc',
        // }),
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 18,
    height: 18,
    borderRadius: 18,
    transition: "200ms ease all",
  },
  "& .MuiSwitch-track": {
    borderRadius: 18,
    opacity: 1,
    backgroundColor: "#22B060",
    boxSizing: "border-box",
    // ...theme.applyStyles('dark', {
    //     backgroundColor: 'rgba(255,255,255,.35)',
    // }),
  },
}));

interface SubscriptionDetailsProps {
  navigate: (path: string) => void;
  state?: {
    subscriptionData?: SubscriptionData;
  };
}

interface PlanPricing {
  attendees: number;
  monthlyPrice: number;
  yearlyPrice: number;
}

interface Tier {
  starting_unit: number;
  ending_unit?: number;
  price: number;
}

// Add this helper component above the SubscriptionDetails component
interface AddonButtonProps {
  isTrialMode: boolean;
  onClick: () => void;
}

const AddonButton: React.FC<AddonButtonProps> = ({ isTrialMode, onClick }) => {
  return (
    <Button
      variant="contained"
      color="primary"
      sx={{
        textTransform: "capitalize",
        borderRadius: "4px",
        px: "12px",
        height: "32px",
      }}
      onClick={onClick}
      disabled={isTrialMode}
    >
      {isTrialMode ? "Unlock Full Access First" : "Add to Plan"}
    </Button>
  );
};

const SubscriptionDetails = ({ navigate, state }: SubscriptionDetailsProps) => {
  const { subscriptionData } = state || {};

  const [renewelPaused, setRenewelPaused] = useState(false);
  const [cancelAccountModal, setCancelAccountModalOpen] = useState(false);
  const [isCancelModalOpen, setCancelModal] = useState(false);
  const [isBillingCycleModalOpen, setIsBillingCycleModalOpen] = useState(false);
  const [cancelAddonModal, setCancelAddonModal] = useState(false);

  const [usageData, setUsageData] = useState(null); // State to store the fetched data
  const [planLoading, setPlanLoading] = useState(true); // State to track loading status
  const [error, setError] = useState(null); // State to handle any errors

  const [liveAttendeesUsageCount, setLiveAttendeesUsageCount] =
    useState<number>(0);
  const [participantsUsageCount, setParticipantsUsageCount] =
    useState<number>(0);
  const [storageUsageValueGB, setStorageUsageValueGB] = useState<number>(0);
  const [storageUsageValue, setStorageUsageValue] = useState<number>(0);
  const [storageUsageUnit, setStorageUsageUnit] = useState<string>("GB");
  const [plans, setPlans] = useState<any[]>([]);
  const [isAnnual, setIsAnnual] = useState(true);
  const [automatedGoldAttendees, setAutomatedGoldAttendees] = useState("500");
  const [allInOneAttendees, setAllInOneAttendees] = useState("500");

  const [liveAttendeesMaxCount, setLiveAttendeesMaxCount] = useState<number>(1);
  const [participantsMaxCount, setParticipantsMaxCount] = useState<number>(1);
  const [storageUsageMaxCount, setStorageUsageMaxCount] = useState<number>(1);

  const [selectedAddonToCancel, setSelectedAddonToCancel] = useState<string | null>(null);

  // Function to get pricing data based on itemPriceId and quantity
  const getPricingData = () => {
    if (!subscriptionData?.subscription_items[0]) return null;

    const { itemPriceId, quantity } = subscriptionData.subscription_items[0];

    // Determine which pricing data to use based on itemPriceId
    let pricingData: Record<string, { price: number | string; webinars: number | string; storage: string }>;
    if (itemPriceId.includes('Automated-Gold')) {
      pricingData = itemPriceId.includes('Yearly') ? automatedPriceYearly : automatedPriceMonthly;
    } else if (itemPriceId.includes('All-In-One')) {
      pricingData = itemPriceId.includes('Yearly') ? allInOnePriceYearly : allInOnePriceMonthly;
    } else {
      return null;
    }

    // Find the appropriate tier based on quantity
    const tierKey = Object.keys(pricingData).find(key => {
      if (key === '10000+' || key === '6000+') return false;
      return parseInt(key) >= quantity;
    });

    if (!tierKey) return null;

    return pricingData[tierKey];
  };

  // Get the filtered pricing data
  const filteredPricingData = getPricingData();

  // Define attendee options similar to PricingSection.tsx
  const allInOneOptions = {
    leads: [100, 300, 1000, 3000, 6000, "6000+"],
    attendees: [50, 100, 500, 1000, 2000, "2000+"]
  };

  // Helper function to find the appropriate tier index
  const findTierIndex = (options: (string | number)[], targetValue: number): number => {
    // First try to find the exact tier
    const exactIndex = options.findIndex(option => {
      const optionValue = typeof option === 'string' ? parseInt(option.replace('+', '')) : option;
      return optionValue >= targetValue;
    });

    if (exactIndex !== -1) {
      return exactIndex;
    }

    // If no exact match, find the highest tier that's less than targetValue
    for (let i = options.length - 1; i >= 0; i--) {
      const optionValue = typeof options[i] === 'string' ? parseInt(options[i].toString().replace('+', '')) : options[i];
      if (typeof optionValue === 'number' && optionValue <= targetValue) {
        return i;
      }
    }

    // Default to first tier if no match found
    return 0;
  };

  // Get the attendee index based on the current plan
  const getAttendeeIndex = () => {
    if (!subscriptionData?.subscription_items[0]) return 0;

    const { itemPriceId, quantity } = subscriptionData.subscription_items[0];

    if (itemPriceId.includes('All-In-One')) {
      return findTierIndex(allInOneOptions.leads, quantity);
    } else {
      // For Automated-Gold, we don't have attendee options, so return 0
      return 0;
    }
  };

  const attendeeIndex = getAttendeeIndex();

  useEffect(() => {
  }, [liveAttendeesMaxCount])

  useEffect(() => {
  }, [participantsMaxCount])

  useEffect(() => {
  }, [storageUsageMaxCount])

  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        const usageResult = await UsageService.fetchUsage();
        setUsageData(usageResult.data);
        if (usageResult && usageResult.data)
          usageResult.data.map((el: any) => {
            if (el.event_id === "attendee_joined") {
              setLiveAttendeesUsageCount(el.event_count);
              setLiveAttendeesMaxCount(el.max_count);
            }
            if (el.event_id === "attendee_registered") {
              setParticipantsUsageCount(el.event_count);
              setParticipantsMaxCount(el.max_count);
            }
            if (el.event_id === "storage_consumed") {
              setStorageUsageValue(el.event_count);
              const unit = el.event_count < 1 ? "MB" : "GB";
              const value =
                el.event_count < 1 ? el.event_count * 1024 : el.event_count;
              setStorageUsageValue(value);
              setStorageUsageUnit(unit);
              setStorageUsageMaxCount(el.event_count < 1 ? el.max_count * 1024 : el.max_count);
            }
          });
      } catch (error) {
        setError((error as any).message);
      }
    };

    const fetchPlansData = async () => {
      try {
        const plansResult = await ChargebeeService.getPlans();
        setPlans(plansResult.data || []);
      } catch (error) {
        setError((error as any).message);
      }
    };

    Promise.all([fetchUsageData(), fetchPlansData()]).finally(() =>
      setPlanLoading(false)
    );
  }, []);

  const getStatusDisplay = (subscription: any) => {
    if (!subscription) return { text: "Unknown", color: "#666666" };

    switch (subscription.status) {
      case "active":
        return { text: "Active", color: "#178EFC" };
      case "cancelled":
        return { text: "Cancelled", color: "#FF4242" };
      case "in_trial":
        return { text: "Trial", color: "#22B060" };
      case "non_renewing":
        return { text: "Non-Renewing", color: "#FF4242" };
      default:
        return { text: subscription.status, color: "#666666" };
    }
  };

  const calculateNextBillingAmount = () => {
    if (!subscriptionData) return 0;

    let totalCents = 0;

    subscriptionData.subscription_items.forEach((item) => {
      totalCents += item.amount || 0;
    });

    return (totalCents / 100).toFixed(2);
  };

  // Error handling useEffect
  React.useEffect(() => {
    if (!subscriptionData) {
      console.error("No subscription data provided");
      navigate("/subscription"); // Redirect back if no data
    }
  }, [subscriptionData, navigate]);

  function handleClick(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    event.preventDefault();
  }

  const isPlanTrial = subscriptionData?.subscription_items.some(
    (item) =>
      item.itemType === "plan" &&
      item.itemPriceId.toLowerCase().includes("trial")
  );
  const showTrialSection = isPlanTrial;
  const showActiveSection = !isPlanTrial;

  // Function to get pricing options from plans
  const getPricingOptions = () => {
    const automatedGoldMonthly = plans.find(
      (p) => p.id === "Automated-Gold-USD-Monthly"
    );
    const automatedGoldYearly = plans.find(
      (p) => p.id === "Automated-Gold-USD-Yearly"
    );
    const allInOneMonthly = plans.find(
      (p) => p.id === "All-In-One-USD-Monthly"
    );
    const allInOneYearly = plans.find((p) => p.id === "All-In-One-USD-Yearly");

    // Get unique attendee tiers
    const attendeeOptions =
      automatedGoldMonthly?.item_price.tiers
        .filter((tier: Tier) => tier.ending_unit)
        .map((tier: Tier) => tier.ending_unit!.toString()) || [];

    return {
      attendeeOptions,
      getPrice: (planId: string, attendees: number) => {
        const plan = plans.find((p) => p.id === planId);
        const tier = plan?.item_price.tiers.find(
          (t: Tier) =>
            attendees >= t.starting_unit &&
            (!t.ending_unit || attendees <= t.ending_unit)
        );
        return tier ? tier.price / 100 : 0; // Convert cents to dollars
      },
    };
  };

  const { attendeeOptions, getPrice } = getPricingOptions();

  // Toggle between monthly and annual
  const handlePlanToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsAnnual(event.target.checked);
    // if (event.target.checked) {
    //   setIsBillingCycleModalOpen(true);
    // } else {
    //   setIsAnnual(false);
    // }
  };

  // Separate handlers for each plan
  const handleAutomatedGoldAttendees = (selected: string) => {
    setAutomatedGoldAttendees(selected);
  };

  const handleAllInOneAttendees = (selected: string) => {
    setAllInOneAttendees(selected);
  };

  const handleBillingCycleConfirm = () => {
    setIsAnnual(true);
    setIsBillingCycleModalOpen(false);
  }
    // Add logic to handle the billing cycle change
  // Add this helper function to determine if the current plan is annual
  const isCurrentPlanAnnual = () => {
    const currentPlanId = subscriptionData?.subscription_items.find(
      (item) => item.itemType === "plan"
    )?.itemPriceId || "";
    
    return currentPlanId.includes("Yearly");
  };

  // Add this helper function to get the correct addon suffix
  const getAddonSuffix = () => {
    return isCurrentPlanAnnual() ? "USD-Yearly" : "USD-Monthly";
  };

  // Add this method before handleUpgradePlan
  const handleUnlockFullAccess = () => {
    // Get current plan ID without "Trial" in it
    const currentPlanId = subscriptionData?.subscription_items.find(
      (item) => item.itemType === "plan"
    )?.itemPriceId || "";
    
    // Extract plan details
    const planType = currentPlanId.includes("Automated-Gold") ? "Automated-Gold" : "All-In-One";
    const currency = "USD";
    const billingCycle = currentPlanId.includes("Yearly") ? "Yearly" : "Monthly";
    const quantity = subscriptionData?.subscription_items.find(
      (item) => item.itemType === "plan"
    )?.quantity || 200;
    
    // Create the proper item ID format
    const itemId = `${planType}-${currency}-${billingCycle}-${quantity}`;
    
    // Call the upgrade plan method with the formatted item ID
    handleUpgradePlan(itemId);
  };

  if (!subscriptionData) {
    return null;
  }

  const scrollToAddOns = () => {
    const addOnsSection = document.getElementById("available-add-ons");
    if (addOnsSection) {
      addOnsSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToUpgradePlan = () => {
    const pricingSection = document.getElementById("pricing-section");
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Update handleUpgradePlan to use selectedPlanId
  const handleUpgradePlan = async (itemId: string) => {
    console.log("itemId", itemId);
    try {
      const subscriptionId = subscriptionData?.subscription?.id;
      
      // Determine if this is a plan or an add-on
      const isPlanUpgrade = itemId.includes('Automated-Gold') || itemId.includes('All-In-One');
      
      // For add-ons, append the appropriate suffix if needed (except for custom-domain)
      if (!isPlanUpgrade && 
          !itemId.includes('USD-Monthly') && 
          !itemId.includes('USD-Yearly') && 
          itemId !== "custom-domain") {
        itemId = `${itemId}-${getAddonSuffix()}`;
      }
      
      // Extract units from itemId if it contains a number at the end
      const unitsMatch = itemId.match(/-(\d+)$/);
      const units = unitsMatch ? parseInt(unitsMatch[1]) : undefined;
      
      // Remove the units from itemId if present
      const baseItemId = unitsMatch ? itemId.slice(0, -(unitsMatch[0].length)) : itemId;
      
      // Prepare parameters for the API call
      const params: any = {
        subscriptionId,
        planId: baseItemId,
      };

      // Only include units and trialEnd for plan upgrades
      if (isPlanUpgrade) {
        const finalUnits = units || (baseItemId.includes("Automated-Gold")
          ? parseInt(automatedGoldAttendees)
          : parseInt(allInOneAttendees));
        
        params.units = finalUnits;
        params.trialEnd = 0; // 0 to end trial immediately
      }

      const hostedPage = await ChargebeeService.upgradePlanUsingCheckout(
        params.subscriptionId,
        params.planId,
        params.units,
        params.trialEnd
      );

      if (hostedPage?.hosted_page?.url) {
        window.location.href = hostedPage.hosted_page.url;
      } else {
        console.error("No hosted page URL received");
      }
    } catch (error) {
      console.error("Error upgrading plan:", error);
    }
  };

  const handleCancelAddon = async (addonId: string) => {
    try {
      const subscriptionId = subscriptionData?.subscription?.id;
      
      if (!subscriptionId) {
        console.error("No subscription ID available");
        return;
      }
      
      console.log("Cancelling addon:", addonId, "for subscription:", subscriptionId);
      
      // Call ChargebeeService to cancel the addon
      const result = await ChargebeeService.openEditSubscriptionSection(subscriptionId);

    } catch (error) {
      console.error("Error cancelling addon:", error);
      toast.error("An error occurred while cancelling the add-on");
    } finally {
      setCancelAddonModal(false);
    }
  };


  return (
    <Container sx={{ maxWidth: "1500px !important", px: "0!important" }}>
      <div role="presentation" onClick={handleClick}>
        <Breadcrumbs
          aria-label="subsciption details breadcrumb"
          sx={{
            color: "#178EFC",
            fontSize: "14px",
            paddingTop: "0 !important",
            "& li:last-child a": { color: "#000000" },
          }}
        >
          <Link
            underline="hover"
            color="#178EFC"
            href="/"
            onClick={() => {
              navigate("/subscription");
            }}
          >
            Subscriptions
          </Link>
          <Link underline="hover" href="#">
            Easywebinar
          </Link>
        </Breadcrumbs>
      </div>

      {/* updated design for this details section */}
      <Paper
        sx={{
          p: { xs: "16px", sm: "24px" },
          mt: "28px",
          borderRadius: "8px",
          border: "1px solid #E8EAF0",
          boxShadow: "none",
        }}
      >

        <Box
          display={"flex"}
          justifyContent={"space-between"}
          flexWrap={"wrap"}
          gap={"16px"}
        >
          <Box display={"flex"} alignItems={"center"}>
            <Typography
              variant="h2"
              color="initial"
              display={"inline"}
              fontSize={"24px"}
              fontWeight={"500"}
            >
              {formatPlanName(subscriptionData?.subscription_items
                .find((item) => item.itemType === "plan")
                ?.itemPriceId || "-- --")}
            </Typography>
            {subscriptionData?.subscription && (
              <Box
                display={"inline-flex"}
                width={"fit-content"}
                p={"3px 12px"}
                borderRadius={"10px"}
                bgcolor={`${getStatusDisplay(subscriptionData.subscription).color}0f`}
                color={getStatusDisplay(subscriptionData.subscription).color}
                ml={3}
                fontSize={"16px"}
                fontWeight={"500"}
              >
                {getStatusDisplay(subscriptionData.subscription).text}
              </Box>
            )}
          </Box>

          {!showActiveSection && <Button
            variant="contained"
            sx={{ mt: { xs: "16px", sm: "0" }, borderRadius: "8px" }}
            onClick={scrollToUpgradePlan}
            size="large"
          >
            Upgrade Now
          </Button>}

          {/* unlock full access */}
          {subscriptionData?.subscription?.status === "in_trial" && (
            <Paper sx={{
              p: "24px",
              borderRadius: "8px",
              border: `2px solid ${theme.palette.primary.main}`,
              bgcolor: "#F2F9FC",
              boxShadow: "none",
              display: { xs: "block", sm: "flex" },
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: { xs: "wrap", md: "nowrap" },
              width: "100%",
            }}>
              <Box flex={1} maxWidth={700} mr={3}>
                <Typography variant="subtitle1" color="initial" fontWeight={"500"} mb={1} fontSize={`min(calc(16px + 2 * (100vw - 400px) / (1920 - 400)), 18px)`}>
                  Limited Trial Access
                </Typography>
                <Typography variant="body1" color={theme.palette.text.secondary} fontSize={`min(calc(14px + 2 * (100vw - 400px) / (1920 - 400)), 16px)`}>You currently have limited access to this plan's features. Your trial ends on <Typography component="span" color={theme.palette.primary.main} fontWeight={"500"}>{formatDate(subscriptionData?.subscription?.planExpireDate)}</Typography>. Upgrade anytime to unlock full access, or continue with restricted limits until your trial expires.</Typography>
              </Box>
              <Button
                variant="contained"
                sx={{ mt: { xs: "16px", sm: "0" }, borderRadius: "8px" }}
                onClick={handleUnlockFullAccess}
                size="large"
              >
                Get Full Access
              </Button>
            </Paper>
          )}
          {/* unlock full access */}

        </Box>
        <Grid container spacing={{ xs: 2.25 }} mt="18px">
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 4 }}>
            <Box
              p="24px 31px"
              borderRadius="8px"
              border="2px solid #E8EAF0"
              height={"100%"}
            >
              <Typography variant="body1" color={theme.palette.text.secondary} mb="16px">Next Billing Date</Typography>
              <Typography
                variant="subtitle1"
                display={"flex"}
                alignItems={"center"}
                gap="12px"
                fontWeight={"500"}
                fontSize={`min(calc(16px + 0.5vw), 20px)`}
              >
                <CalenderIcon />
                {formatDate(subscriptionData?.subscription?.nextBillingAt)}
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, lg: 4, xl: 4 }} order={{ xs: 2, lg: 0 }}>
            <Box
              p="24px 31px"
              borderRadius="8px"
              border="2px solid #E8EAF0"
              height={"100%"}
            >
              <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} mb="16px" flexWrap={"wrap"} gap={"8px"}>
                <Typography variant="body1" color={theme.palette.text.secondary} >Billing Cycle</Typography>

                {/* ------------------------IMPLEMENT IN FUTURE ------------------------ */}
                {/* <FormGroup>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Typography color="#22B060" fontWeight="500" variant="body2">
                      Monthly
                    </Typography>
                    <AntSwitch
                      checked={isAnnual}
                      onChange={handlePlanToggle}
                      inputProps={{ "aria-label": "ant design" }}
                    />
                    <Typography display="flex" alignItems="center" gap={1} variant="body2">
                      Annual
                      <Box
                        sx={{
                          bgcolor: "#E7F9EF",
                          color: "#22B060",
                          p: "4px 10px",
                          borderRadius: "100px",
                          fontSize: "12px",
                          display: "inline-flex",
                          alignItems: "center"
                        }}
                      >
                        Save 17%
                      </Box>
                    </Typography>
                  </Stack>
                </FormGroup> */}
                {/* ------------------------IMPLEMENT IN FUTURE ------------------------ */}

              </Box>
              <Typography
                variant="subtitle1"
                display={"flex"}
                alignItems={"center"}
                gap="12px"
                fontWeight={"500"}
                fontSize={`min(calc(16px + 0.5vw), 20px)`}
              >
                <TimerIconMd />
                {subscriptionData?.subscription_items.find(
                  (item) => item.itemType === "plan"
                )?.itemPriceId.includes("Yearly") ? "Annual" : "Monthly"}
              </Typography>
            </Box></Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 4 }}><Box
            p="24px 31px"
            borderRadius="8px"
            border="2px solid #E8EAF0"
            height={"100%"}
          >
            <Typography variant="body1" color={theme.palette.text.secondary} mb="16px">Monthly Pricing</Typography>
            <Typography
              variant="subtitle1"
              display={"flex"}
              alignItems={"center"}
              gap="12px"
              fontWeight={"500"}
              fontSize={`min(calc(16px + 0.5vw), 20px)`}
            >
              $ {calculateNextBillingAmount()}
            </Typography>
          </Box> </Grid>

          <Grid size={{ xs: 12, md: 8, lg: 8 }} order={{ xs: 3, lg: 0 }}><Box
            p="24px 31px"
            borderRadius="8px"
            border="2px solid #E8EAF0"
            height={"100%"}
          >
            <Typography variant="h6" color={theme.palette.text.primary} mb="16px">Included Features</Typography>
            <Grid container spacing={{ xs: 0, sm: 2 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2.25}>
                  <Box minWidth={"24px"} display="flex"><TickWithCircle /></Box>
                  <Typography variant="body2" color="#333333" mt="2px" fontSize="min(calc(16px + 2 * (100vw - 400px) / (1920 - 400)), 18px)">
                    Up to {subscriptionData?.subscription_items[0]?.quantity || participantsMaxCount || 100} leads
                  </Typography>
                </Box>
                {!subscriptionData?.subscription_items.find(
                  (item) => item.itemType === "plan"
                )?.itemPriceId.includes("Automated-Gold") &&
                  <Box display="flex" alignItems="center" gap={1} mb={2.25}>
                    <Box minWidth={"24px"} display="flex"><TickWithCircle /></Box>
                    <Typography variant="body2" color="#333333" mt="2px" fontSize="min(calc(16px + 2 * (100vw - 400px) / (1920 - 400)), 18px)">
                      Up to {subscriptionData?.subscription_items.find(
                        (item) => item.itemType === "plan"
                      )?.itemPriceId.includes("All-In-One") ? allInOneOptions.attendees[attendeeIndex] : ""} live attendees
                    </Typography>
                  </Box>
                }
                <Box display="flex" alignItems="center" gap={1} mb={2.25}>
                  <Box minWidth={"24px"} display="flex"><TickWithCircle /></Box>
                  <Typography variant="body2" color="#333333" mt="2px" fontSize="min(calc(16px + 2 * (100vw - 400px) / (1920 - 400)), 18px)">
                    Up to {filteredPricingData ? filteredPricingData.webinars : 3} pre-recorded webinars
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1} mb={2.25}>
                  <Box minWidth={"24px"} display="flex"><TickWithCircle /></Box>
                  <Typography variant="body2" color="#333333" mt="2px" fontSize="min(calc(16px + 2 * (100vw - 400px) / (1920 - 400)), 18px)">
                    Up to 4 presenters
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2.25}>
                  <Box minWidth={"24px"} display="flex"><TickWithCircle /></Box>
                  <Typography variant="body2" color="#333333" mt="2px" fontSize="min(calc(16px + 2 * (100vw - 400px) / (1920 - 400)), 18px)">
                    Just in time
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1} mb={2.25}>
                  <Box minWidth={"24px"} display="flex"><TickWithCircle /></Box>
                  <Typography variant="body2" color="#333333" mt="2px" fontSize="min(calc(16px + 2 * (100vw - 400px) / (1920 - 400)), 18px)">
                    Integrations / Zapier integration
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1} mb={2.25}>
                  <Box minWidth={"24px"} display="flex"><TickWithCircle /></Box>
                  <Typography variant="body2" color="#333333" mt="2px" fontSize="min(calc(16px + 2 * (100vw - 400px) / (1920 - 400)), 18px)">
                    Clone webinar
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box></Grid>
          <Grid size={{ xs: 12, md: 4 }} order={{ xs: 4, lg: 0 }}><Box
            p="24px 31px"
            borderRadius="8px"
            border="2px solid #E8EAF0"
            height={"100%"}
          >
            <Box
              className="storage-box-con"
              p={{ sx: "0 0 20px", xl: "0 20px 20px" }}
              display={"flex"}
              justifyContent="center"
              flexDirection="column"
              alignItems="center"
            >
              <Typography
                variant="h5"
                color="#000000"
                fontWeight={"500"}
                textAlign="start"
                width="100%"
              >
                Storage
              </Typography>
              <Box
                className="storage-box"
                sx={{ position: "relative" }}
                height={"100%"}
                maxHeight="142px"
                width={"100%"}
                maxWidth={"142px"}
                flexDirection="column"
                alignItems="center"
              >
                <CircularProgress
                  variant="determinate"
                  value={storageUsageMaxCount > 0 ? (Math.ceil((storageUsageValue / storageUsageMaxCount) * 100) > 100 ? 100 : Math.ceil((storageUsageValue / storageUsageMaxCount) * 100)) : 0}
                  size={"100%"}
                  color="primary"
                  sx={(theme) => ({
                    color: storageUsageMaxCount > 0 && Math.ceil((storageUsageValue / storageUsageMaxCount) * 100) > 100 ? "#E44258" : "#22B060",
                    animationDuration: "550ms",
                    position: "absolute",
                    left: 0,
                    top: 0,
                    zIndex: 1,
                    [`& .${circularProgressClasses.circle}`]: {
                      strokeLinecap: "round",
                      color: storageUsageMaxCount > 0 && Math.ceil((storageUsageValue / storageUsageMaxCount) * 100) > 100 ? "#E44258" : "#22B060",
                    },
                    ...theme.applyStyles("dark", {
                      color: "#308fe8",
                    }),
                  })}
                />
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={"100%"}
                  color="primary"
                  sx={(theme) => ({
                    color: "#E8E8EA",
                    animationDuration: "550ms",
                    position: "absolute",
                    left: 0,
                    top: 0,
                    [`& .${circularProgressClasses.circle}`]: {
                      strokeLinecap: "round",
                      color: "#E8E8EA",
                    },
                    ...theme.applyStyles("dark", {
                      color: "#E8E8EA",
                    }),
                  })}
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: "absolute",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{
                      color: storageUsageMaxCount > 0 && Math.ceil((storageUsageValue / storageUsageMaxCount) * 100) > 100 ? "#E44258" : "#333333",
                      fontSize: "18px",
                      fontWeight: "500",
                    }}
                  >
                    {storageUsageMaxCount > 0 ? Math.ceil(
                      (storageUsageValue / storageUsageMaxCount) * 100
                    ).toString() : "0"}
                    % Used
                  </Typography>
                </Box>
              </Box>
              <Box mt="16px">
                <Box display={"flex"} alignItems="center" mb={2}>
                  <Box
                    className="small-cirle"
                    bgcolor={"#E8E8EA"}
                    borderRadius={"100%"}
                    height={"15px"}
                    width={"15px"}
                    mr={2}
                  ></Box>
                  <Typography
                    variant="subtitle1"
                    color="#000000"
                    fontSize={{ sm: "14px", md: "16px" }}
                  >
                    {storageUsageMaxCount} {storageUsageUnit} Total
                  </Typography>
                </Box>
                <Box display={"flex"} alignItems="center">
                  <Box
                    className="small-cirle"
                    bgcolor={"#22B060"}
                    borderRadius={"100%"}
                    height={"15px"}
                    width={"15px"}
                    mr={2}
                  ></Box>
                  <Typography variant="subtitle1" color="#000000">
                    {storageUsageValue.toFixed(2)} {storageUsageUnit} Used
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="center" mt="24px">
                  <Button variant="contained" color="primary" size="large" sx={{ borderRadius: "8px" }} onClick={scrollToAddOns}>
                    Buy More
                  </Button>
                </Box>

              </Box>
            </Box>
          </Box> </Grid>

          {showActiveSection && <Grid size={12} order={{ xs: 5, lg: 0 }}><Box
            p="24px 31px 32px"
            borderRadius="8px"
            border="2px solid #E8EAF0"
            height={"100%"}
          >
            <Typography variant="h6" color={theme.palette.text.primary} mb="16px">Plan Usage</Typography>
            <Box width={"100%"}>
              <Box display={"flex"} justifyContent={"space-between"}>
                <Typography
                  color="#333333"
                  variant="body2"
                  mb="6px"
                  textAlign="left"
                  display="flex"
                  alignItems="center"
                >
                  Leads
                  <Tooltip arrow title="Overage Charges: $0.50 per lead above your plan limit">
                    <Box component="span" sx={{ display: 'inline-flex', ml: 1 }}>
                      <InfoBlueIcon />
                    </Box>
                  </Tooltip>

                  {participantsMaxCount > 0 && Math.ceil((participantsUsageCount / participantsMaxCount) * 100) > 90 ? (
                    <Box display="flex" alignItems="center" gap={1} ml="20px" borderRadius="4px" bgcolor="#FEF9F1" p="4px 8px">
                      <AlertIcon />
                      <Typography color={theme.palette.text.tertiary} fontSize="12px">
                        To get more leads for your account <Typography component="a" onClick={scrollToUpgradePlan} fontSize="inherit" color={theme.palette.primary.main} sx={{ textDecoration: "underline", cursor: "pointer" }}>Upgrade your Plan</Typography> or overages apply
                      </Typography>
                      <Tooltip arrow title="Overage Charges: $0.50 per lead above your plan limit">
                        <Box component="span" sx={{ display: 'inline-flex', ml: 1 }}>
                          <InfoBlueIcon />
                        </Box>
                      </Tooltip>
                    </Box>
                  ) : null}
                </Typography>
                <Typography
                  color="#333333"
                  fontSize="12px"
                  mb="6px"
                  textAlign="left"
                  sx={{
                    color: participantsMaxCount > 0 && Math.ceil((participantsUsageCount / participantsMaxCount) * 100) > 90 ? "#E44258" : undefined
                  }}
                >
                  {participantsUsageCount} of {participantsMaxCount} ({participantsMaxCount > 0 ? Math.ceil((participantsUsageCount / participantsMaxCount) * 100) : 0}% used)
                </Typography>
              </Box>
              <BorderLinearProgressGreen
                variant="determinate"
                value={participantsMaxCount > 0 ? Math.ceil((participantsUsageCount / participantsMaxCount) * 100) : 0}
                isHighUsage={participantsMaxCount > 0 && Math.ceil((participantsUsageCount / participantsMaxCount) * 100) > 90}
                sx={{
                  display: participantsMaxCount > 0 ? 'block' : 'none',
                  "& span": {
                    transform: participantsMaxCount > 0 && Math.ceil((participantsUsageCount / participantsMaxCount) * 100) > 100 ? 'translateX(0%) !important' : ''
                  }
                }}
              />
            </Box>
            <Box width={"100%"} mt="40px">
              <Box display={"flex"} justifyContent={"space-between"}>
                <Typography
                  color={subscriptionData?.subscription_items.find(
                    (item) => item.itemType === "plan"
                  )?.itemPriceId.includes("Automated-Gold") ? "#999999" : "#333333"}
                  variant="body2"
                  mb="6px"
                  textAlign="left"
                  display="flex"
                  alignItems="center"
                >
                  Live Attendees
                  {subscriptionData?.subscription_items.find(
                    (item) => item.itemType === "plan"
                  )?.itemPriceId.includes("Automated-Gold") ? (
                    <Tooltip arrow title="Upgrade to All-in-One plan to access Live Attendee features">
                      <Box component="span" sx={{ display: 'inline-flex', ml: 1 }}>
                        <InfoBlueIcon />
                      </Box>
                    </Tooltip>
                  ) : (
                    <Tooltip arrow title="Overage Charges: $0.20 per live attendee above your plan limit">
                      <Box component="span" sx={{ display: 'inline-flex', ml: 1 }}>
                        <InfoBlueIcon />
                      </Box>
                    </Tooltip>
                  )}
                  {liveAttendeesMaxCount > 0 && Math.ceil((liveAttendeesUsageCount / liveAttendeesMaxCount) * 100) > 90 ? (
                    <Box display="flex" alignItems="center" gap={1} ml="20px" borderRadius="4px" bgcolor="#FEF9F1" p="4px 8px">
                      <AlertIcon />
                      <Typography color={theme.palette.text.tertiary} fontSize="12px">
                        To get more leads for your account <Typography component="a" onClick={scrollToUpgradePlan} fontSize="inherit" color={theme.palette.primary.main} sx={{ textDecoration: "underline", cursor: "pointer" }}>Upgrade your Plan</Typography> or overages apply
                      </Typography>
                      <Tooltip arrow title="Overage Charges: $0.20 per live attendee above your plan limit">
                        <Box component="span" sx={{ display: 'inline-flex', ml: 1 }}>
                          <InfoBlueIcon />
                        </Box>
                      </Tooltip>
                    </Box>
                  ) : null}
                </Typography>
                <Typography
                  color={subscriptionData?.subscription_items.find(
                    (item) => item.itemType === "plan"
                  )?.itemPriceId.includes("Automated-Gold") ? "#999999" : (liveAttendeesMaxCount > 0 && Math.ceil((liveAttendeesUsageCount / liveAttendeesMaxCount) * 100) > 90 ? "#E44258" : "#333333")}
                  fontSize="12px"
                  mb="6px"
                  textAlign="left"
                >
                  {subscriptionData?.subscription_items.find(
                    (item) => item.itemType === "plan"
                  )?.itemPriceId.includes("Automated-Gold")
                    ? "Not available"
                    : `${liveAttendeesUsageCount} of ${liveAttendeesMaxCount} (${liveAttendeesMaxCount > 0 ? Math.ceil((liveAttendeesUsageCount / liveAttendeesMaxCount) * 100) : 0}% used)`}
                </Typography>
              </Box>
              <BorderLinearProgressGreen
                variant="determinate"
                value={subscriptionData?.subscription_items.find(
                  (item) => item.itemType === "plan"
                )?.itemPriceId.includes("Automated-Gold")
                  ? 0
                  : (liveAttendeesMaxCount > 0 ? Math.ceil((liveAttendeesUsageCount / liveAttendeesMaxCount) * 100) : 0)}
                isHighUsage={liveAttendeesMaxCount > 0 && Math.ceil((liveAttendeesUsageCount / liveAttendeesMaxCount) * 100) > 90}
                sx={{
                  opacity: subscriptionData?.subscription_items.find(
                    (item) => item.itemType === "plan"
                  )?.itemPriceId.includes("Automated-Gold") ? 0.5 : 1,
                  display: liveAttendeesMaxCount > 0 ? 'block' : 'none',
                  "& span": {
                    transform: liveAttendeesMaxCount > 0 && Math.ceil((liveAttendeesUsageCount / liveAttendeesMaxCount) * 100) > 100 ? 'translateX(0%) !important' : ''
                  }
                }}
              />
            </Box>
          </Box></Grid>}
        </Grid>
      </Paper>
      {/* updated design for this details section */}

      {/* Trial Section - Show only in Trial mode and if plan includes "trial" */}
      {showTrialSection && (
        <Box
          display="flex"
          gap={{ xs: "17px", xl: "48px" }}
          flexWrap={{ xs: "wrap", sm: "unset" }}
        >
          <Paper
            sx={{
              p: "24px",
              mt: "39px",
              borderRadius: "8px",
              border: "1px solid #E8EAF0",
              boxShadow: "none",
              width: { xs: "100%", sm: "50%" },
            }}
          >
            <Box
              display={"flex"}
              justifyContent={"space-between"}
              flexWrap={"wrap"}
              gap={"16px"}
            >
              <Box>
                <Typography variant="body2" color="#333333">
                  Plan Details
                </Typography>
                <Typography
                  variant="h5"
                  color="initial"
                  fontWeight={"500"}
                  mt={1}
                >
                  Free trial
                </Typography>
              </Box>
            </Box>
            <Box
              display={"flex"}
              mt="32px"
              flexWrap={"wrap"}
              gap={{ xs: "16px", xl: "unset" }}
            >
              <Box
                minWidth={"184px"}
                width={{ xs: "17%", sm: "100%", xl: "17%" }}
              >
                <Typography variant="body2" color="#333333">
                  Start Date
                </Typography>
                <Typography
                  variant="h6"
                  color="initial"
                  fontWeight={"500"}
                  mt={1}
                >
                  {formatDate(subscriptionData.subscription.trialStartDate)}
                </Typography>
              </Box>
              <Box
                minWidth={"184px"}
                width={{ xs: "17%", sm: "100%", xl: "17%" }}
              >
                <Typography variant="body2" color="#333333">
                  Trial End Date
                </Typography>
                <Typography
                  variant="h6"
                  color="initial"
                  fontWeight={"500"}
                  mt={1}
                >
                  {formatDate(subscriptionData.subscription.planExpireDate)}
                </Typography>
              </Box>
            </Box>
          </Paper>
          <Paper
            sx={{
              p: "24px",
              mt: { xs: "11px", sm: "39px" },
              borderRadius: "8px",
              border: "1px solid #E8EAF0",
              boxShadow: "none",
              width: { xs: "100%", sm: "50%" },
            }}
          >
            <Box
              display="flex"
              flexWrap="wrap"
              gap={{ xs: "32px", xl: "unset" }}
            >
              <Box
                sx={{
                  width: {
                    xs: "100%",
                    sm: "100%",
                    md: "100%",
                    // lg: "40%",
                    xl: "40%",
                  },
                }}
              >
                <Typography
                  variant="h5"
                  color="initial"
                  fontWeight={"500"}
                  mb={1}
                >
                  Plan Usage
                </Typography>
                <Box flexWrap={"wrap"}>
                  <Box width={"100%"}>
                    <Box display={"flex"} justifyContent={"space-between"}>
                      <Typography
                        color="#333333"
                        fontSize="12px"
                        mb="4px"
                        textAlign="left"
                      >
                        Leads
                      </Typography>
                      <Typography
                        color="#333333"
                        fontSize="12px"
                        mb="6px"
                        textAlign="left"
                      >
                        {participantsUsageCount} of {participantsMaxCount} ({participantsMaxCount > 0 ? Math.ceil((participantsUsageCount / participantsMaxCount) * 100) : 0}% used)
                      </Typography>
                    </Box>
                    <BorderLinearProgressGreen
                      variant="determinate"
                      value={participantsMaxCount > 0 ? Math.ceil((participantsUsageCount / participantsMaxCount) * 100) : 0}
                      isHighUsage={participantsMaxCount > 0 && Math.ceil((participantsUsageCount / participantsMaxCount) * 100) > 90}
                      sx={{ display: participantsMaxCount > 0 ? 'block' : 'none' }}
                    />
                    <Box
                      display={"flex"}
                      justifyContent={"space-between"}
                      mt="24px"
                    >
                      <Typography
                        color="#333333"
                        fontSize="12px"
                        mb="4px"
                        textAlign="left"
                      >
                        Live Attendees
                      </Typography>
                      <Typography
                        color="#333333"
                        fontSize="12px"
                        mb="6px"
                        textAlign="left"
                      >
                        {liveAttendeesUsageCount} of {liveAttendeesMaxCount} ({liveAttendeesMaxCount > 0 ? Math.ceil((liveAttendeesUsageCount / liveAttendeesMaxCount) * 100) : 0}% used)
                      </Typography>
                    </Box>
                    <BorderLinearProgressGreen
                      variant="determinate"
                      value={liveAttendeesMaxCount > 0 ? Math.ceil((liveAttendeesUsageCount / liveAttendeesMaxCount) * 100) : 0}
                      isHighUsage={liveAttendeesMaxCount > 0 && Math.ceil((liveAttendeesUsageCount / liveAttendeesMaxCount) * 100) > 90}
                      sx={{ display: liveAttendeesMaxCount > 0 ? 'block' : 'none' }}
                    />
                  </Box>
                </Box>
              </Box>
              <Box
                className="storage-box-con"
                gap={{ xs: "6px", md: "unset" }}
                p={{ sx: "0 0 20px", xl: "0 20px 0px" }}
                display={"flex"}
                flexWrap="wrap"
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: {
                    xs: "100%",
                    sm: "100%",
                    md: "100%",
                    // lg: "40%",
                    xl: "60%",
                  },
                }}
              >
                <Box
                  className="storage-box"
                  sx={{ position: "relative" }}
                  height={"100%"}
                  maxHeight="142px"
                  width={"100%"}
                  maxWidth={"142px"}
                  mr={{ xs: "16px", xl: "36px" }}
                >
                  <CircularProgress
                    variant="determinate"
                    value={storageUsageMaxCount > 0 ? (Math.ceil((storageUsageValue / storageUsageMaxCount) * 100) > 100 ? 100 : Math.ceil((storageUsageValue / storageUsageMaxCount) * 100)) : 0}
                    size={"100%"}
                    color="primary"
                    sx={(theme) => ({
                      color: storageUsageMaxCount > 0 && Math.ceil((storageUsageValue / storageUsageMaxCount) * 100) > 100 ? "#E44258" : "#22B060",
                      animationDuration: "550ms",
                      position: "absolute",
                      left: 0,
                      top: 0,
                      zIndex: 1,
                      [`& .${circularProgressClasses.circle}`]: {
                        strokeLinecap: "round",
                        color: storageUsageMaxCount > 0 && Math.ceil((storageUsageValue / storageUsageMaxCount) * 100) > 100 ? "#E44258" : "#22B060",
                      },
                      ...theme.applyStyles("dark", {
                        color: "#308fe8",
                      }),
                    })}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={"100%"}
                    color="primary"
                    sx={(theme) => ({
                      color: "#E8E8EA",
                      animationDuration: "550ms",
                      position: "absolute",
                      left: 0,
                      top: 0,
                      [`& .${circularProgressClasses.circle}`]: {
                        strokeLinecap: "round",
                        color: "#E8E8EA",
                      },
                      ...theme.applyStyles("dark", {
                        color: "#E8E8EA",
                      }),
                    })}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: "absolute",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        color: storageUsageMaxCount > 0 && Math.ceil((storageUsageValue / storageUsageMaxCount) * 100) > 100 ? "#E44258" : "#333333",
                        fontSize: "18px",
                        fontWeight: "500",
                      }}
                    >
                      {storageUsageMaxCount > 0 ? Math.ceil(
                        (storageUsageValue / storageUsageMaxCount) * 100
                      ).toString() : "0"}
                      % Used
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography
                    variant="h5"
                    color="#000000"
                    fontWeight={"500"}
                    mb={"21px"}
                    textAlign="center"
                  >
                    Storage
                  </Typography>
                  <Box display={"flex"} alignItems="center" mb={2}>
                    <Box
                      className="small-cirle"
                      bgcolor={"#E8E8EA"}
                      borderRadius={"100%"}
                      height={"15px"}
                      width={"15px"}
                      mr={2}
                    ></Box>
                    <Typography
                      variant="subtitle1"
                      color="#000000"
                      fontSize={{ sm: "14px", md: "16px" }}
                    >
                      {storageUsageMaxCount} {storageUsageUnit} Total
                    </Typography>
                  </Box>
                  <Box display={"flex"} alignItems="center">
                    <Box
                      className="small-cirle"
                      bgcolor={"#22B060"}
                      borderRadius={"100%"}
                      height={"15px"}
                      width={"15px"}
                      mr={2}
                    ></Box>
                    <Typography variant="subtitle1" color="#000000">
                      {storageUsageValue.toFixed(2)} {storageUsageUnit} Used
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      {/* pricing table v2 */}
      <Box id="pricing-section">
        <PricingSection
          currentPlanId={subscriptionData?.subscription_items.find(
            (item) => item.itemType === "plan"
          )?.itemPriceId}
          currentAttendees={subscriptionData?.subscription_items.find(
            (item) => item.itemType === "plan"
          )?.quantity}
          onPlanSelect={(planId) => {
            handleUpgradePlan(planId);
          }}
        />
      </Box>
      {/* ==================pricing table ================= */}

      {/* ===================subscribed add ons ================ */}
      {subscriptionData?.subscription_items.some(item => item.itemType === "addon") && (
        <Paper sx={{
          p: "24px",
          mt: "39px",
          borderRadius: "8px",
          border: "1px solid #E8EAF0",
          boxShadow: "none",
        }}>
          <Typography variant="h5" color="initial" fontWeight={"500"} mb={3}>
            Subscribed Add-Ons
          </Typography>
          <Grid
            container
            rowSpacing={{ xs: 3, sm: 3, md: 3 }}
            columnSpacing={{ xs: 3, sm: 2, md: 3 }}
          >
            {/* Subscribed Add-ons */}
            {subscriptionData?.subscription_items
              .filter(item => item.itemType === "addon")
              .map((addon) => (
                <Grid key={addon.itemPriceId} size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                  <Card sx={{
                    padding: "26px 23px",
                    border: "1px solid #E8EAF0",
                    boxShadow: "0px 4px 12px 0px rgba(43, 43, 48, 0.08)",
                    display: "flex",
                    flexDirection: "column !important",
                    justifyContent: "space-between",
                    position: "relative",
                    height: "100%",
                    borderRadius: "8px",
                    minHeight: "300px",
                  }}>
                    <Box>
                      <Box mb="24px" display="flex" gap="18px" alignItems="center">
                        {addon.itemPriceId.includes('White-Label-Branding') && <img src={file} />}
                        {addon.itemPriceId.includes('Storage-Add-on-2-GB') && <StorageIcon />}
                        {addon.itemPriceId.includes('One-Time-Webinar-Assist-and-Setup') && <SupportAgentIcon />}
                        {addon.itemPriceId.includes('custom-domain') && <img src={file} />}
                        <Typography fontSize="18px" fontWeight="500" color="#333333">
                          {formatPlanName(addon.itemPriceId)}
                        </Typography>
                      </Box>
                      <Typography color="#333333" fontSize="14px" lineHeight="24px" mb="24px">
                        {addon.itemPriceId.includes('White-Label-Branding') &&
                          "Remove EasyWebinar branding and customize the platform with your own brand identity for a seamless, professional appearance."}
                        {addon.itemPriceId.includes('Storage-Add-on-2-GB') &&
                          "Expand your webinar storage capacity with additional space for recordings, presentations, and resources to ensure seamless content delivery."}
                        {addon.itemPriceId.includes('One-Time-Webinar-Assist-and-Setup') &&
                          "Get expert assistance in setting up your webinar from start to finish. Our team will help configure your settings, customize templates, and ensure everything runs smoothly."}
                        {addon.itemPriceId.includes('custom-domain') &&
                          "Personalize your webinar experience with a custom domain that reflects your brand's identity."}
                      </Typography>
                    </Box>
                    <Box>
                      <Box display="flex" alignItems="start" justifyContent="space-between">
                        <Typography color="#666666" fontSize="14px">
                          Renews on {formatDate(subscriptionData.subscription.nextBillingAt)}
                        </Typography>
                        <Typography color="#22B060" fontSize="20px" fontWeight="600" display="flex" alignItems="end">
                          ${(addon.unitPrice / 100).toFixed(2)}
                          <Typography color="#333333" fontSize="14px" ml="4px" mb="2px">
                            / month
                          </Typography>
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="end" mt="12px">
                        <Button
                          variant="outlined"
                          color="error"
                          sx={{
                            height: "32px",
                            fontSize: "14px",
                            px: "12px",
                            borderRadius: "4px",
                            textTransform: "capitalize",
                          }}
                          onClick={() => {
                            setSelectedAddonToCancel(addon.itemPriceId);
                            setCancelAddonModal(true);
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}

            {/* Add More Card */}
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
              <Card
                sx={{
                  padding: "26px 23px",
                  border: "1px solid #E8EAF0",
                  boxShadow: "0px 4px 12px 0px rgba(43, 43, 48, 0.08)",
                  display: "flex",
                  flexDirection: "column !important",
                  justifyContent: "center",
                  position: "relative",
                  height: "100%",
                  borderRadius: "8px",
                  minHeight: "300px",
                }}
              >
                <Box display="flex" flexDirection="column">
                  <Box
                    display="flex"
                    alignItems="center"
                    flexDirection="column"
                    justifyContent="center"
                  >
                    <Box
                      display="flex"
                      sx={{ "& img": { maxWidth: "100%", height: "auto" } }}
                    >
                      <img
                        src="https://easywebinar-site.s3.us-east-1.amazonaws.com/images/autheticator-app-images/Illustration.webp"
                        alt="illustration-img"
                      />
                    </Box>
                    <Typography color="#111824" fontSize="14px" my="24px">
                      Explore more add-on features
                    </Typography>
                    <Box textAlign="center">
                      <Button
                        sx={{
                          textTransform: "capitalize",
                          bgcolor: "#178EFC",
                          color: "#FFFFFF !important",
                          px: "24px",
                          height: "40px",
                          fontSize: "16px",
                          cursor: "pointer",
                          borderRadius: "8px",
                          "&:hover": {
                            border: "none !important",
                            bgcolor: "#178EFC",
                          },
                        }}
                        onClick={scrollToAddOns}
                      >
                        Add More
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}
      {/* ===================available add ons ================ */}
      <Box mt="42px" id="available-add-ons">
        <Typography variant="h5" color="#333333" fontWeight="500" mb="24px">
          Available Add-Ons
        </Typography>
        {subscriptionData?.subscription?.status === "in_trial" && (
          <Paper sx={{
            p: "16px 24px",
            mb: "24px",
            borderRadius: "8px",
            border: `1px solid ${theme.palette.primary.main}`,
            bgcolor: "#F2F9FC",
            boxShadow: "none",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: { xs: "wrap", md: "nowrap" },
          }}>
            <Box flex={1} maxWidth={700} mr={3}>
              <Typography variant="body1" color={theme.palette.text.secondary} fontSize="16px">
                Add-ons are only available after upgrading to full access.
              </Typography>
            </Box>
            <Button
              variant="contained"
              sx={{ mt: { xs: "16px", sm: "0" }, borderRadius: "8px", whiteSpace: "nowrap" }}
              onClick={handleUnlockFullAccess}
              size="medium"
            >
              Get Full Access
            </Button>
          </Paper>
        )}
        <Box display="flex" gap="20px" flexWrap="wrap">
          {/* Filter out subscribed add-ons */}
          {!subscriptionData?.subscription_items.some(item =>
            item.itemPriceId.includes("White-Label-Branding")) && (
              <Paper
                sx={{
                  maxWidth: "calc(100% / 3 - 14px)",
                  minWidth: "366px",
                  width: "100%",
                  minHeight: "280px",
                  p: "24px 15px",
                  borderRadius: "8px",
                  border: "1px solid #E8EAF0",
                  boxShadow: "none",
                  "@media (min-width:768px) and (max-width:1366px)": {
                    width: "47%",
                  },
                  "@media (min-width:320px) and (max-width:600px)": {
                    maxWidth: "100%",
                  },
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  flexDirection="column"
                  height="100%"
                >
                  <Box mb="24px">
                    <Box mb="24px" display="flex" gap="18px" alignItems="center">
                      <img src={file} />
                      <Typography fontSize="18px" fontWeight="500" color="#333333">
                        White Label
                      </Typography>
                    </Box>
                    <Typography color="#333333" fontSize="14px" lineHeight="24px">
                      Remove EasyWebinar branding and customize the platform with
                      your own brand identity for a seamless, professional
                      appearance.
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap="16px"
                  >
                    <Typography
                      display="flex"
                      alignItems="end"
                      fontSize="20px"
                      color="#22B060"
                      fontWeight="600"
                    >
                      $99
                      <Typography color="#333333" fontSize="14px" ml="4px" mb="2px">
                        / month
                      </Typography>
                    </Typography>
                    <AddonButton 
                      isTrialMode={subscriptionData?.subscription?.status === "in_trial"}
                      onClick={() => handleUpgradePlan("White-Label-Branding")}
                    />
                  </Box>
                </Box>
              </Paper>
            )}

          {!subscriptionData?.subscription_items.some(item =>
            item.itemPriceId.includes("Storage-Add-on-2-GB")) && (
              <Paper
                sx={{
                  maxWidth: "calc(100% / 3 - 14px)",
                  minWidth: "366px",
                  width: "100%",
                  minHeight: "280px",
                  p: "24px 15px",
                  borderRadius: "8px",
                  border: "1px solid #E8EAF0",
                  boxShadow: "none",
                  "@media (min-width:768px) and (max-width:1366px)": {
                    width: "47%",
                  },
                  "@media (min-width:320px) and (max-width:600px)": {
                    maxWidth: "100%",
                  },
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  flexDirection="column"
                  height="100%"
                >
                  <Box mb="24px">
                    <Box mb="24px" display="flex" gap="18px" alignItems="center">
                      <StorageIcon />
                      <Typography fontSize="18px" fontWeight="500" color="#333333">
                        Additional Storage (5GB)
                      </Typography>
                    </Box>
                    <Typography color="#333333" fontSize="14px" lineHeight="24px">
                      Expand your webinar storage capacity with additional space for
                      recordings, presentations, and resources to ensure seamless
                      content delivery.
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap="16px"
                  >
                    <Typography
                      display="flex"
                      alignItems="end"
                      fontSize="20px"
                      color="#22B060"
                      fontWeight="600"
                    >
                      $11
                      <Typography color="#333333" fontSize="14px" ml="4px" mb="2px">
                        / month
                      </Typography>
                    </Typography>
                    <AddonButton 
                      isTrialMode={subscriptionData?.subscription?.status === "in_trial"}
                      onClick={() => handleUpgradePlan("Storage-Add-on-2-GB")}
                    />
                  </Box>
                </Box>
              </Paper>
            )}

          {!subscriptionData?.subscription_items.some(item =>
            item.itemPriceId.includes("One-Time-Webinar-Assist-and-Setup")) && (
              <Paper
                sx={{
                  maxWidth: "calc(100% / 3 - 14px)",
                  minWidth: "366px",
                  width: "100%",
                  minHeight: "280px",
                  p: "24px 15px",
                  borderRadius: "8px",
                  border: "1px solid #E8EAF0",
                  boxShadow: "none",
                  "@media (min-width:768px) and (max-width:1366px)": {
                    width: "47%",
                  },
                  "@media (min-width:320px) and (max-width:600px)": {
                    maxWidth: "100%",
                  },
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  flexDirection="column"
                  height="100%"
                >
                  <Box mb="24px">
                    <Box mb="24px" display="flex" gap="18px" alignItems="center">
                      <SupportAgentIcon />
                      <Typography fontSize="18px" fontWeight="500" color="#333333">
                        One Time Webinar Assist & Setup
                      </Typography>
                    </Box>
                    <Typography color="#333333" fontSize="14px" lineHeight="24px">
                      Get expert assistance in setting up your webinar from start to
                      finish. Our team will help configure your settings, customize
                      templates, and ensure everything runs smoothly.
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap="16px"
                  >
                    <Typography
                      display="flex"
                      alignItems="end"
                      fontSize="20px"
                      color="#22B060"
                      fontWeight="600"
                    >
                      $250
                      <Typography color="#333333" fontSize="14px" ml="4px" mb="2px">
                        / month
                      </Typography>
                    </Typography>
                    <AddonButton 
                      isTrialMode={subscriptionData?.subscription?.status === "in_trial"}
                      onClick={() => handleUpgradePlan("One-Time-Webinar-Assist-and-Setup")}
                    />
                  </Box>
                </Box>
              </Paper>
            )}

          {!subscriptionData?.subscription_items.some(item =>
            item.itemPriceId === "custom-domain") && (
              <Paper
                sx={{
                  maxWidth: "calc(100% / 3 - 14px)",
                  minWidth: "366px",
                  width: "100%",
                  minHeight: "280px",
                  p: "24px 15px",
                  borderRadius: "8px",
                  border: "1px solid #E8EAF0",
                  boxShadow: "none",
                  "@media (min-width:768px) and (max-width:1366px)": {
                    width: "47%",
                  },
                  "@media (min-width:320px) and (max-width:600px)": {
                    maxWidth: "100%",
                  },
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  flexDirection="column"
                  height="100%"
                >
                  <Box mb="24px">
                    <Box mb="24px" display="flex" gap="18px" alignItems="center">
                      <img src={file} />
                      <Typography fontSize="18px" fontWeight="500" color="#333333">
                        C-Name
                      </Typography>
                    </Box>
                    <Typography color="#333333" fontSize="14px" lineHeight="24px">
                      Personalize your webinar experience with a custom domain that
                      reflects your brand's identity.
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap="16px"
                  >
                    <Typography
                      display="flex"
                      alignItems="end"
                      fontSize="20px"
                      color="#22B060"
                      fontWeight="600"
                    >
                      $49
                      <Typography color="#333333" fontSize="14px" ml="4px" mb="2px">
                        / month
                      </Typography>
                    </Typography>
                    <AddonButton 
                      isTrialMode={subscriptionData?.subscription?.status === "in_trial"}
                      onClick={() => handleUpgradePlan("custom-domain")}
                    />
                  </Box>
                </Box>
              </Paper>
            )}
        </Box>
      </Box>
      {/* ===================footer-section ================ */}
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        mt={{ xs: "25px", sm: "42px" }}
      >
        <Typography color="#666666" fontSize="14px">
          Got a question? Get in touch with our
          <a
            href="#"
            style={{
              color: "#178EFC",
              marginLeft: "4px",
              textDecoration: "none",
            }}
          >
            Support Team
          </a>
        </Typography>
        <Button
          variant="outlined"
          color="error"
          sx={{ textTransform: "capitalize", marginLeft: "35px" }}
          onClick={() => setCancelAccountModalOpen(true)}
        >
          Cancel Account
        </Button>
        <CancelAccountModal
          open={cancelAccountModal}
          onClose={() => setCancelAccountModalOpen(false)}
          subscriptionId={subscriptionData?.subscription?.id}
        />
        <CancelSubscription
          open={cancelAddonModal}
          onClose={() => setCancelAddonModal(false)}
          onConfirm={() => {
            if (selectedAddonToCancel) {
              handleCancelAddon(selectedAddonToCancel);
            }
          }}
          addonName={selectedAddonToCancel ? formatPlanName(selectedAddonToCancel) : ""}
        />
      </Box>
      {/* ===================footer-section ================ */}

      <BillingCycleModal
        open={isBillingCycleModalOpen}
        onClose={() => setIsBillingCycleModalOpen(false)}
        onConfirm={handleBillingCycleConfirm}
        proRatedAmount={50}
        annualAmount={490}
      />
    </Container>
  );
};

export default SubscriptionDetails;
